import axios from 'axios'
import { API } from '~/app/routes'
import { getExtensionFromName, isAllowedChatExtension } from '~/messages/attachmentUtils'
export const CHAT_UPLOAD_TIMEOUT_MS = 120000
export const CHAT_MAX_ATTACHMENTS = 5
export const CHAT_MAX_FILE_SIZE_MB = 50

type UploadChatImagesResponse = {
    success?: boolean
    urls?: string[]
    msg?: string
}

export type UploadChatImagesProgress = (percent: number) => void

export const validateChatAttachments = (
    files: File[],
    allowedExtensions: string[],
): string | null => {
    if (!files.length) {
        return null
    }
    if (files.length > CHAT_MAX_ATTACHMENTS) {
        return 'maxFiles'
    }
    const maxBytes = CHAT_MAX_FILE_SIZE_MB * 1024 * 1024
    for (const file of files) {
        if (file.size > maxBytes) {
            return 'maxSize'
        }
        const ext = getExtensionFromName(file.name)
        if (!isAllowedChatExtension(ext, allowedExtensions)) {
            return 'invalidType'
        }
    }
    return null
}

export const uploadChatImages = async (
    files: File[],
    issueId: string | number,
    type: number,
    allowedExtensions: string[],
    onUploadProgress?: UploadChatImagesProgress,
): Promise<string[]> => {
    const validationError = validateChatAttachments(files, allowedExtensions)
    if (validationError) {
        throw new Error(validationError)
    }

    const formData = new FormData()
    files.forEach(file => {
        formData.append('files[]', file, file.name)
    })
    formData.append('issueId', String(issueId))
    formData.append('type', String(type))

    const data = await axios.post<null, UploadChatImagesResponse>(API.chatUpload, formData, {
        timeout: CHAT_UPLOAD_TIMEOUT_MS,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: event => {
            if (!onUploadProgress || !event.total) {
                return
            }
            onUploadProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)))
        },
    })

    if (!data?.success || !Array.isArray(data.urls)) {
        throw new Error(data?.msg || 'uploadFailed')
    }

    return data.urls
}
